import { Avatar, AvatarImage } from '@radix-ui/react-avatar';
import { useMemo, useRef } from 'react';
import IconButton from '@/components/Button/IconButton';
import { Checkbox } from '@/components/Checkbox';
import { HStack, VStack } from '@/components/Layout';
import { Loader } from '@/components/Loading/Loader';
import { Text } from '@/components/Typography';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { cn } from '@/lib/cn';
import { type KeyPrefixSelector, defaultNS, useTranslation } from '@/lib/i18n';
import SelectedOptionChip from './SelectedOptionChip';
import type { ComboboxOption, ComboboxOptionComponent, ComboboxOptionRenderProps, ComboboxOptionRenderer, ComboboxProps } from './types';
import { SelectType, SelectionDisplayStyle } from './types';
import { useCombobox } from './useCombobox';

/**
 * component.Combobox 配下にスコープする selector keyPrefix。
 * 戻り値型を `object` に広げないため、`: KeyPrefixSelector` ではなく `satisfies` を使う。
 */
const comboboxKeyPrefix = ($ => $.component.Combobox) satisfies KeyPrefixSelector<typeof defaultNS>;

/**
 * 検索可能なドロップダウン(popover)選択コンポーネント
 *
 * 機能:
 * - 単一選択/複数選択
 * - 検索フィルタリング
 * - カスタマイズ可能なオプション表示
 * - 無限スクロール対応
 * - キーボード操作対応
 */
export default function Combobox<TOption extends ComboboxOption>(props: ComboboxProps<TOption>) {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: comboboxKeyPrefix
  });
  const {
    selectedOptions,
    onSelect,
    onClearAll,
    onKeyDown
  } = useCombobox(props);
  const isMultiSelect = props.type === SelectType.multiple;
  const multiSelectValues = isMultiSelect ? props.value : [];
  const shouldShowSelections = props.selectionDisplayStyle !== SelectionDisplayStyle.hidden;
  const hasMultiSelectChips = isMultiSelect && shouldShowSelections && multiSelectValues.length > 0;
  const getOptionLabel = useMemo(() => (value: string) => {
    const option = props.options.find(opt => opt.value === value);
    return option?.label ?? props.valueToLabelCache?.get(value) ?? value;
  }, [props.options, props.valueToLabelCache]);
  const filteredOptions = useMemo(() => {
    if (!props.search) return props.options;
    const searchLower = props.search.toLowerCase();
    return props.options.filter(option => {
      if (option.label.toLowerCase().includes(searchLower)) return true;
      if (option.searchKeywords?.some(kw => kw.toLowerCase().includes(searchLower))) return true;
      return false;
    });
  }, [props.options, props.search]) satisfies readonly TOption[];

  // 描画するオプション。maxVisibleOptions 指定時のみ先頭 n 件に切り詰める。
  // 大量候補で CommandItem を全件生成するとメインスレッドが秒単位で止まるため。
  const visibleOptions = useMemo(() => props.maxVisibleOptions != null && filteredOptions.length > props.maxVisibleOptions ? filteredOptions.slice(0, props.maxVisibleOptions) : filteredOptions, [filteredOptions, props.maxVisibleOptions]);
  const truncatedCount = filteredOptions.length - visibleOptions.length;
  const OptionContent = ({
    option
  }: {
    option: TOption;
  }) => {
    if (!props.renderOption) {
      return <HStack className="gap-2 items-center">
          {option.avatarUrl && <Avatar className="w-8">
              <AvatarImage src={option.avatarUrl} className="rounded-sm" />
            </Avatar>}
          <Text variant="label" className="w-full text-wrapper">
            {option.label}
          </Text>
        </HStack>;
    }
    const renderProps = {
      option,
      isSelected: selectedOptions.has(option.value),
      showCheckbox: props.showCheckbox
    } satisfies ComboboxOptionRenderProps<TOption>;
    const isReactComponent = (renderOption: ComboboxOptionComponent<TOption> | ComboboxOptionRenderer<TOption>): renderOption is ComboboxOptionComponent<TOption> => '$$typeof' in renderOption;
    if (isReactComponent(props.renderOption)) {
      const OptionComponent = props.renderOption;
      return <OptionComponent {...renderProps} />;
    }
    return props.renderOption(renderProps);
  };

  /* oxlint-disable react-hooks/exhaustive-deps -- multiSelectValuesは意図的に依存配列に含めている */
  const SelectedValue = useMemo(() => {
    if (!shouldShowSelections) return null;
    if (isMultiSelect) {
      return multiSelectValues.map(value => <SelectedOptionChip key={value} value={value} label={getOptionLabel(value)} onSelect={onSelect} />);
    }
    if (props.value) {
      return <SelectedOptionChip key={props.value} value={props.value} label={getOptionLabel(props.value)} onSelect={onSelect} />;
    }
    return null;
  }, [isMultiSelect, props.value, multiSelectValues, getOptionLabel, onSelect, shouldShowSelections]);
  /* oxlint-enable react-hooks/exhaustive-deps */

  const ClearButton = hasMultiSelectChips && <IconButton icon="XIcon" onClick={onClearAll} variant="mutedGhost" size="ExtraSmall" aria-label={tc('action.clear')} />;
  const LoadingIndicator = props.isFetching && <div className="flex justify-center py-2">
      <Loader />
    </div>;
  const lastOptionRef = useRef<string | null>(null);
  const NoResults = !props.isPending && !props.isFetching && props.noResultsMessage && filteredOptions.length === 0 && <CommandEmpty>{props.noResultsMessage}</CommandEmpty>;
  const sentinel = useIntersectionObserver(() => {
    if (!props.isFetching && props.onScrollToEnd) {
      const lastOption = filteredOptions[filteredOptions.length - 1];
      if (lastOption) {
        const currentKey = lastOption?.key || lastOption.value;
        if (currentKey !== lastOptionRef.current) {
          lastOptionRef.current = currentKey;
          props.onScrollToEnd();
        }
      }
    }
  });
  const renderSearchInput = () => {
    // 複数選択（選択チップは検索窓の上部に別途表示）または未選択の場合は検索入力のみ
    if (isMultiSelect || !shouldShowSelections || !SelectedValue) {
      return <div className="flex-1 min-w-0">
          <CommandInput noBorder className="outline-hidden disabled:cursor-not-allowed disabled:opacity-50 w-full min-w-0" placeholder={props.searchPlaceholder} aria-label={props.searchPlaceholder} value={props.search} onValueChange={props.onSearchChange} onKeyDown={onKeyDown} />
        </div>;
    }

    // 単一選択の場合は、ラベルの長さを判断してレイアウトを変更
    // 選択済みのラベルを取得
    const selectedLabel = props.value ? getOptionLabel(props.value) : '';
    const isLongLabel = selectedLabel.length > 15;

    // 長いラベルの場合は縦レイアウトを使用
    // pl-3 は選択チップ自身に付ける（親に付けない）。選択チップは padding を持たない親
    // （下部の `flex gap-1 items-center pr-2`）に直置きされるため左端が 0 になるが、
    // 親に付けると CommandInput 自身の px-3 と累積して入力だけ 24px ずれる。
    // チップ側に閉じることで CommandInput の px-3 / 複数選択チップ行の px-3 と左端が揃う。
    if (isLongLabel) {
      return <VStack className="flex-1 gap-2 py-2 overflow-hidden">
          <div className="flex flex-wrap gap-1 w-full pl-3">
            {SelectedValue}
          </div>
          <div className="w-full">
            <CommandInput noBorder className="outline-hidden disabled:cursor-not-allowed disabled:opacity-50 w-full min-w-0" placeholder={props.searchPlaceholder} aria-label={props.searchPlaceholder} value={props.search} onValueChange={props.onSearchChange} onKeyDown={onKeyDown} />
          </div>
        </VStack>;
    }

    // 短いラベルの場合は横レイアウトを使用（pl-3 の置き場所の理由は上の縦レイアウトと同じ）
    return <HStack className="flex-1 items-center gap-2 py-2 overflow-hidden">
        <div className="shrink-0 pl-3">{SelectedValue}</div>
        <div className="grow min-w-[120px]">
          <CommandInput noBorder className="outline-hidden disabled:cursor-not-allowed disabled:opacity-50 w-full min-w-0" placeholder={props.searchPlaceholder} aria-label={props.searchPlaceholder} value={props.search} onValueChange={props.onSearchChange} onKeyDown={onKeyDown} />
        </div>
      </HStack>;
  };
  return <Command shouldFilter={false} className="relative">
      {!props.hideSearch && <div className="border-b">
          {hasMultiSelectChips && SelectedValue && <div className="flex flex-wrap gap-1 px-3 pt-2">
              {SelectedValue}
            </div>}
          <div className="flex gap-1 items-center pr-2">
            {renderSearchInput()}
            {ClearButton}
          </div>
        </div>}
      <CommandList id={props.listboxId} className={cn('gap-1 p-1', props.isPlaceholderData && 'opacity-50')}>
        {NoResults}
        {visibleOptions.map((option: TOption, index: number) => <CommandItem key={`${option.value}-${index}`} value={String(option.value)} onSelect={onSelect} disabled={option.disabled} className="cursor-pointer items-center gap-2">
            {props.showCheckbox && <Checkbox checked={selectedOptions.has(option.value)} size={16} disabled={option.disabled} readOnly className="pointer-events-none" />}
            <OptionContent option={option} />
          </CommandItem>)}
        {truncatedCount > 0 && <div className="px-2 py-1.5">
            <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
              {t($ => $.truncatedHint, {
            shown: visibleOptions.length,
            total: filteredOptions.length
          })}
            </Text>
          </div>}
        {LoadingIndicator}
        {props.onScrollToEnd && <div ref={sentinel} role="presentation" aria-hidden="true" />}
      </CommandList>
    </Command>;
}